import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { HookTrackPerformanceTable, VideoPerformance, MetricColumn } from '../HookTrackPerformanceTable';
import { MetricsColumnFilter } from '@/components/metrics/shared/MetricsColumnFilter';
import { PitchSelector } from '@/components/metrics/pitch/PitchSelector';
import { byPositionOptions, usePitchSelection } from '@/components/metrics/pitch/pitchSelection';

interface MetricsTabContentProps {
  videos: VideoPerformance[];
  showTotals?: boolean;
  hasTracking?: boolean;
  onPitchSaved?: () => unknown;
}

// Colunas de rastreamento — só visíveis quando o teste tem integração de conversão
const TRACKING_COLUMN_IDS = ['conversions', 'revenue', 'averageTicket'];

const buildDefaultColumns = (hasTracking: boolean): MetricColumn[] => [
  { id: 'views', label: 'Visualizações', visible: true },
  { id: 'uniqueViews', label: 'Vis. Únicas', visible: true },
  { id: 'plays', label: 'Plays', visible: true },
  { id: 'uniquePlays', label: 'Plays Únicos', visible: true },
  { id: 'playRate', label: 'Play Rate', visible: true },
  { id: 'engagement', label: 'Engajamento', visible: true },
  { id: 'pitchRetention', label: 'Retenção no Pitch', visible: true },
  { id: 'conversions', label: 'Conversões', visible: hasTracking },
  { id: 'revenue', label: 'Receita', visible: hasTracking },
  { id: 'averageTicket', label: 'Ticket Médio', visible: hasTracking },
];

export function MetricsTabContent({ videos, showTotals = true, hasTracking = false, onPitchSaved }: MetricsTabContentProps) {
  useLanguage();
  const [columns, setColumns] = useState<MetricColumn[]>(() => buildDefaultColumns(hasTracking));

  // Seletor por posição (1º pitch, 2º pitch…): cada vídeo do teste tem os próprios pitches.
  const pitchOptions = byPositionOptions(videos.map((video) => video.pitches));
  const [pitchSelection, setPitchSelection] = usePitchSelection(pitchOptions);
  const pitchColumnVisible = !!columns.find((c) => c.id === 'pitchRetention')?.visible;

  // hasTracking pode chegar depois (dados assíncronos): reflete na visibilidade das colunas de conversão
  useEffect(() => {
    setColumns((prev) =>
      prev.map((c) => (TRACKING_COLUMN_IDS.includes(c.id) ? { ...c, visible: hasTracking } : c))
    );
  }, [hasTracking]);

  return (
    <div className="space-y-6">
      {/* Modal Premium de Métricas */}
      <div className="bg-white rounded-[14px] shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-[#E7E9EF] overflow-hidden">
        {/* Header interno com filtros */}
        <div className="px-8 pt-8 pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-1">
              {translateInterfaceText("Desempenho por vídeo")}</h2>
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("Acompanhe as métricas individuais de cada vídeo deste teste.")}</p>
          </div>
          
          {/* Seletor de pitch + filtro de colunas */}
          <div className="flex flex-shrink-0 items-center gap-3">
            {pitchColumnVisible && (
              <PitchSelector options={pitchOptions} value={pitchSelection} onChange={setPitchSelection} />
            )}
            <MetricsColumnFilter 
              columns={columns} 
              onColumnsChange={setColumns} 
            />
          </div>
        </div>

        {/* Tabela Premium */}
        <div className="px-8 pb-8">
          <HookTrackPerformanceTable 
            onPitchSaved={onPitchSaved}
            pitchPosition={Number(pitchSelection ?? 0)}
            showPitchName={pitchOptions.length >= 2}
            videos={videos} 
            columns={columns}
            showTotals={showTotals} 
          />
        </div>
      </div>
    </div>
  );
}
